/**
 * usePush — client-side web-push lifecycle: permission, subscription, and
 * server registration. Returns everything the Notifications settings page needs.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // push-sw.js lives at site root → scope covers the whole app
  return navigator.serviceWorker.register('/push-sw.js');
}

export function usePush() {
  const [supported, setSupported] = useState(true);
  const [enabled, setEnabled] = useState<boolean | null>(null); // null = checking
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSupported);
    if (!isSupported) { setPermission('unsupported'); setEnabled(false); return; }
    setPermission(Notification.permission);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
      if (!reg) { setEnabled(false); return; }
      const sub = await reg.pushManager.getSubscription();
      setEnabled(!!sub);
    } catch {
      setEnabled(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const enable = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    setBusy(true);
    try {
      // 1. Ask permission (must be from a user gesture — the button click)
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return { ok: false, error: 'Notification permission was not granted. Check your browser/phone settings.' };

      // 2. Get the server's VAPID public key
      const keyRes: any = await api.get('/push/vapid-key');
      const publicKey = (keyRes.data ?? keyRes).publicKey;
      if (!publicKey) return { ok: false, error: 'Push notifications are not configured on the server yet.' };

      // 3. Subscribe this browser
      const reg = await registerServiceWorker();
      if (!reg) return { ok: false, error: 'This browser does not support push notifications.' };
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      // 4. Register with our server
      const raw = sub.toJSON();
      await api.post('/push/subscribe', { subscription: raw });
      setEnabled(true);
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.message || e?.message || 'Could not enable notifications.' };
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        try { await api.post('/push/unsubscribe', { subscription: { endpoint: sub.endpoint } }); } catch { /* best effort */ }
        await sub.unsubscribe();
      }
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    try {
      await api.post('/push/test');
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.message || 'Test notification failed.' };
    }
  }, []);

  return { supported, enabled, permission, busy, enable, disable, sendTest, recheck: check };
}
